<template>
  <div class="payment-form-wrapper">
    <header class="page-header fade-in">
      <button class="btn btn-light" @click="goBack">
        <i class="bi bi-arrow-left"></i>
        <span>Retour</span>
      </button>
      <div>
        <h1>Modifier le paiement</h1>
        <p v-if="form.payment_reference">
          Référence {{ form.payment_reference }}
        </p>
      </div>
    </header>

    <section class="page-body fade-in" v-if="!initialLoading">
      <form class="form-grid" @submit.prevent="handleSubmit">
        <div class="form-card">
          <h2>Détails du paiement</h2>
          <div class="form-group">
            <label>Référence *</label>
            <input
              v-model.trim="form.payment_reference"
              type="text"
              class="form-control"
              :class="{ 'has-error': errors.reference }"
            />
            <p v-if="errors.reference" class="error">{{ errors.reference }}</p>
          </div>

          <div class="form-group">
            <label>Vente *</label>
            <select
              v-model="form.seller_sale_reference"
              class="form-control"
              :class="{ 'has-error': errors.seller_sale_reference }"
            >
              <option value="" disabled>Choisir une vente</option>
              <option
                v-for="sale in unpaidSales"
                :key="sale.reference"
                :value="sale.reference"
              >
                {{ sale.reference }} — Total: {{ formatCurrency(sale.total) }} —
                Restant: {{ formatCurrency(sale.unpaidAmount) }}
              </option>
            </select>
            <p v-if="errors.seller_sale_reference" class="error">
              {{ errors.seller_sale_reference }}
            </p>
          </div>

          <div class="form-group">
            <label>Montant *</label>
            <input
              v-model.number="form.amount"
              type="number"
              min="0"
              step="0.01"
              class="form-control"
              :class="{ 'has-error': errors.amount }"
            />
            <p v-if="errors.amount" class="error">{{ errors.amount }}</p>
          </div>
        </div>

        <div class="form-card">
          <h2>Informations complémentaires</h2>
          <div class="form-group">
            <label>Date de paiement *</label>
            <input
              v-model="form.date"
              type="date"
              class="form-control"
              :class="{ 'has-error': errors.date }"
            />
            <p v-if="errors.date" class="error">
              {{ errors.date }}
            </p>
          </div>

          <div class="form-group">
            <label>Remarque</label>
            <textarea
              v-model.trim="form.remark"
              rows="4"
              class="form-control"
            ></textarea>
          </div>
        </div>

        <div class="form-actions">
          <button
            type="button"
            class="btn btn-light"
            @click="goBack"
            :disabled="submitting"
          >
            Annuler
          </button>
          <button type="submit" class="btn btn-primary" :disabled="submitting">
            <i class="bi bi-save"></i>
            <span v-if="!submitting">Enregistrer</span>
            <span v-else>Mise à jour...</span>
          </button>
        </div>
      </form>
    </section>

    <div v-else class="loading-state">
      <div class="loader"></div>
      <p>Chargement du paiement...</p>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

export default {
  name: "PaymentEdit",
  data() {
    return {
      submitting: false,
      initialLoading: true,
      errors: {},
      form: {
        payment_reference: "",
        seller_sale_reference: "",
        amount: "",
        date: "",
        remark: "",
      },
    };
  },
  computed: {
    ...mapGetters("sellerPayment", {
      payment: "getSellerPayment",
    }),
    ...mapGetters("sellerSale", {
      sales: "getAll",
    }),
    normalizedSales() {
      const toNumber = (value, fallback = 0) => {
        if (value === null || value === undefined || value === "") {
          return fallback;
        }
        const number = Number(value);
        return Number.isNaN(number) ? fallback : number;
      };
      const rawSales = Array.isArray(this.sales?.data)
        ? this.sales.data
        : this.sales || [];

      return rawSales.map((entry) => {
        const reference =
          entry.reference || entry.sale_reference || entry.id || "";
        const total = toNumber(
          entry.totalPriceTTC != null
            ? entry.totalPriceTTC
            : entry.total_price_ttc != null
            ? entry.total_price_ttc
            : entry.total_amount != null
            ? entry.total_amount
            : entry.total,
          0
        );
        const amountPaid = toNumber(
          entry.amountPaid != null
            ? entry.amountPaid
            : entry.amount_paid != null
            ? entry.amount_paid
            : entry.payrollAmount,
          0
        );
        const unpaidAmount = toNumber(
          entry.amountUnpaid != null
            ? entry.amountUnpaid
            : entry.amount_unpaid != null
            ? entry.amount_unpaid
            : entry.remaining_amount != null
            ? entry.remaining_amount
            : total - amountPaid,
          0
        );

        return {
          id: entry.id || entry.uuid || reference,
          reference,
          seller_reference:
            entry.seller_reference ||
            entry.seller?.reference ||
            entry.sellerReference ||
            null,
          total,
          amountPaid,
          unpaidAmount,
        };
      });
    },
    unpaidSales() {
      return this.normalizedSales.filter((sale) => sale.unpaidAmount > 0);
    },
    reference() {
      return this.$route.params.reference;
    },
  },
  watch: {
    payment: {
      handler(newValue) {
        if (!newValue) return;
        this.form = {
          payment_reference:
            newValue.payment_reference || newValue.reference || "",
          seller_sale_reference:
            newValue.seller_sale_reference || newValue.sale?.reference || "",
          amount: newValue.amount || 0,
          date: newValue.date || newValue.payment_date || "",
          remark: newValue.remark || newValue.notes || "",
        };
        this.initialLoading = false;
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    goBack() {
      this.$router.back();
    },
    formatCurrency(amount) {
      if (!amount && amount !== 0) return "—";
      const number = Number(amount);
      if (Number.isNaN(number)) {
        return amount;
      }
      return number.toLocaleString("fr-FR", {
        style: "currency",
        currency: "MAD",
      });
    },
    validate() {
      const errors = {};
      if (!this.form.payment_reference) {
        errors.reference = "La référence est obligatoire.";
      }
      if (!this.form.seller_sale_reference) {
        errors.seller_sale_reference = "Veuillez sélectionner une vente.";
      }
      if (this.form.amount === "" || this.form.amount === null) {
        errors.amount = "Indiquez le montant du paiement.";
      } else if (Number(this.form.amount) <= 0) {
        errors.amount = "Le montant doit être supérieur à 0.";
      }
      if (!this.form.date) {
        errors.date = "Sélectionnez la date du paiement.";
      }
      this.errors = errors;
      return Object.keys(errors).length === 0;
    },
    async loadData() {
      try {
        await Promise.all([
          this.$store.dispatch("sellerPayment/show", this.reference),
          this.sales && this.sales.length
            ? Promise.resolve()
            : this.$store.dispatch("sellerSale/getAll"),
        ]);
      } catch (error) {
        console.error("Error loading payment", error);
        this.$toast.error("Impossible de charger le paiement.");
        this.initialLoading = false;
      }
    },
    async handleSubmit() {
      if (!this.validate()) return;
      this.submitting = true;
      try {
        // Get the selected sale to extract seller_reference
        const selectedSale = this.unpaidSales.find(
          (sale) => sale.reference === this.form.seller_sale_reference
        );

        // Get user reference from auth
        const user = this.$store.getters["auth/user"];
        const userReference = user?.reference || user?.user_reference || null;

        // Build payload with all required fields
        const payload = {
          payment_reference: this.form.payment_reference,
          seller_sale_reference: this.form.seller_sale_reference,
          seller_reference: selectedSale?.seller_reference || null,
          user_reference: userReference,
          amount: Number(this.form.amount),
          date: this.form.date,
          remark: this.form.remark || null,
        };

        await this.$store.dispatch("sellerPayment/update", {
          reference: this.reference,
          data: payload,
        });
        this.$toast.success("Paiement mis à jour avec succès.");
        this.$router.push({
          name: "payments-show",
          params: { reference: this.reference },
        });
      } catch (error) {
        console.error("Error updating payment", error);
        this.$toast.error("Impossible de mettre à jour le paiement.");
      } finally {
        this.submitting = false;
      }
    },
  },
  mounted() {
    this.loadData();
  },
};
</script>

<style scoped>
.payment-form-wrapper {
  min-height: 100vh;
  background: linear-gradient(180deg, #f7f8fb 0%, #f0f2f8 100%);
  padding: 2rem 2.5rem 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.page-header {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.page-header h1 {
  margin: 0 0 0.35rem;
  font-size: 2.15rem;
  font-weight: 700;
  color: #0f172a;
}

.page-header p {
  margin: 0;
  color: #556073;
}

.page-body {
  background: #ffffff;
  border-radius: 24px;
  padding: 2rem;
  box-shadow: 0 24px 45px rgba(15, 23, 42, 0.08);
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1.5rem;
}

.form-card {
  background: #f9f9ff;
  border-radius: 20px;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow: 0 12px 24px rgba(99, 102, 241, 0.08);
}

.form-card h2 {
  margin: 0;
  font-size: 1.3rem;
  color: #1f2937;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

label {
  font-weight: 600;
  color: #475569;
}

.form-control {
  border-radius: 12px;
  border: 1px solid #d1d5db;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.form-control:focus {
  outline: none;
  border-color: #6366f1;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.has-error {
  border-color: #ef4444;
}

.error {
  color: #ef4444;
  font-size: 0.85rem;
}

.form-actions {
  grid-column: 1 / -1;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.1rem;
  border-radius: 14px;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.btn:hover {
  transform: translateY(-1px);
}

.btn-light {
  background: rgba(148, 163, 184, 0.15);
  color: #1f2937;
}

.btn-primary {
  background: linear-gradient(135deg, #2563eb, #4f46e5);
  color: white;
  box-shadow: 0 10px 22px rgba(79, 70, 229, 0.25);
}

.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  min-height: 60vh;
  color: #6b7280;
}

.loader {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 4px solid #d1d5db;
  border-top-color: #2563eb;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .form-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .payment-form-wrapper {
    padding: 1.25rem 1rem 4rem;
  }
  .page-body {
    padding: 1.5rem;
  }
  .form-card {
    padding: 1.25rem;
  }
  .form-actions {
    flex-direction: column-reverse;
    align-items: stretch;
  }
  .btn {
    width: 100%;
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .page-header h1 {
    font-size: 1.75rem;
  }
}
</style>
